import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {ViewType} from "../../view_type/ViewType";
import {IStartRegistrationFlowPayload, startRegistrationFlow} from "../state/registration_flow_slice";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {useUnlockChoiceExperiment} from "./use_unlock_choice_experiment";

export type UnlockChoiceCtaRequest =
    | {kind: "lead"; payload: MainLeadDataPayload; gtmClickText: string; gtmSourceSection: string}
    | {kind: "floor_plan"; propertyId?: number}
    | {kind: "vendor_report"; vendorId: number; offerId?: number; propertyId?: number; amplitudePlacement?: AmplitudeAuthPlacement}
    | {kind: "construction_photos"; hasContent: boolean};

export interface IUseUnlockChoiceCta {
    /** Side effect free: true when `startUnlockChoice` called with the same request would take over the click */
    isInterceptable: (request: UnlockChoiceCtaRequest) => boolean;
    /** Returns true when the unlock choice flow was started and the caller must skip its own click behaviour */
    startUnlockChoice: (request: UnlockChoiceCtaRequest) => boolean;
}

export const useUnlockChoiceCta = (): IUseUnlockChoiceCta => {
    const dispatch = useAppDispatch();
    const viewType = useAppSelector((state) => state.viewType.current);
    const pageProperty = useAppSelector((state) => state.property.property);
    const {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {isResolved, isAssigned, isTreatment, trackExposure} = useUnlockChoiceExperiment({isEnabled: true});

    const isVerifiedUser = isUserLoggedIn && hasCompleteProfile && hasPhoneVerified;
    const isVariantDecided = isResolved && isAssigned;

    const getVerifiedContentFlow = (
        unlockChoiceEntryPoint: UnlockChoiceEntryPoint,
        ids: {propertyId?: number; offerId?: number; vendorId?: number},
        amplitudePlacement: AmplitudeAuthPlacement | undefined
    ): IStartRegistrationFlowPayload | null => {
        if (!isVerifiedUser || ids.offerId === undefined || !ids.vendorId) {
            return null;
        }

        return {
            isUserLoggedIn: true,
            hasCompleteProfile: true,
            hasPhoneVerified: true,
            unlockChoiceEntryPoint,
            unlockChoiceOrigin: "cta",
            propertyId: ids.propertyId,
            offerId: ids.offerId,
            vendorId: ids.vendorId,
            amplitudePlacement
        };
    };

    const getEligibleFlow = (request: UnlockChoiceCtaRequest): IStartRegistrationFlowPayload | null => {
        if (viewType !== ViewType.PROPERTY) {
            return null;
        }

        switch (request.kind) {
            case "lead": {
                const {payload, gtmClickText, gtmSourceSection} = request;

                if (payload.offerId === null || !payload.vendorId) {
                    return null;
                }

                return {
                    isUserLoggedIn,
                    hasCompleteProfile,
                    hasPhoneVerified,
                    userSource: UserSource.APPLICATION,
                    gtmClickText,
                    gtmSourceSection,
                    modalVariant: "hidden_prices_variant_b",
                    propertyId: payload.propertyId ?? undefined,
                    offerId: payload.offerId,
                    vendorId: payload.vendorId,
                    leadSource: payload.source,
                    leadSourceSection: payload.sourceSection,
                    amplitudePlacement: payload.amplitudePlacement,
                    unlockChoiceEntryPoint: UnlockChoiceEntryPoint.LEAD,
                    unlockChoiceOrigin: "cta",
                    unlockChoiceLeadPayload: payload
                };
            }
            case "floor_plan":
                if (!pageProperty || !pageProperty.plan || request.propertyId !== pageProperty.id) {
                    return null;
                }

                return getVerifiedContentFlow(
                    UnlockChoiceEntryPoint.FLOOR_PLAN,
                    {
                        propertyId: pageProperty.id,
                        offerId: pageProperty.offer.id,
                        vendorId: pageProperty.offer.vendor.id
                    },
                    "rp_download_floor_plan"
                );
            case "vendor_report":
                return getVerifiedContentFlow(
                    UnlockChoiceEntryPoint.VENDOR_REPORT,
                    {
                        propertyId: request.propertyId,
                        offerId: request.offerId,
                        vendorId: request.vendorId
                    },
                    request.amplitudePlacement
                );
            case "construction_photos":
                if (!pageProperty || !request.hasContent) {
                    return null;
                }

                return getVerifiedContentFlow(
                    UnlockChoiceEntryPoint.CONSTRUCTION_PHOTOS,
                    {
                        propertyId: pageProperty.id,
                        offerId: pageProperty.offer.id,
                        vendorId: pageProperty.offer.vendor.id
                    },
                    "rp_construction_photos"
                );
            default:
                return null;
        }
    };

    const isInterceptable = (request: UnlockChoiceCtaRequest) => isVariantDecided && isTreatment && getEligibleFlow(request) !== null;

    const startUnlockChoice = (request: UnlockChoiceCtaRequest) => {
        if (!isVariantDecided) {
            return false;
        }

        const flow = getEligibleFlow(request);

        if (flow === null) {
            return false;
        }

        trackExposure();

        if (!isTreatment) {
            return false;
        }

        dispatch(startRegistrationFlow(flow));

        return true;
    };

    return {isInterceptable, startUnlockChoice};
};
import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {ViewType} from "../../view_type/ViewType";
import {IStartRegistrationFlowPayload, startRegistrationFlow} from "../state/registration_flow_slice";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {useUnlockChoiceExperiment} from "./use_unlock_choice_experiment";

export type UnlockChoiceCtaRequest =
    | {kind: "lead"; payload: MainLeadDataPayload; gtmClickText: string; gtmSourceSection: string}
    | {kind: "floor_plan"; propertyId?: number}
    | {kind: "vendor_report"; vendorId: number; offerId?: number; propertyId?: number; amplitudePlacement?: AmplitudeAuthPlacement}
    | {kind: "construction_photos"; hasContent: boolean};

export interface IUseUnlockChoiceCta {
    /** Side effect free: true when `startUnlockChoice` called with the same request would take over the click */
    isInterceptable: (request: UnlockChoiceCtaRequest) => boolean;
    /** Returns true when the unlock choice flow was started and the caller must skip its own click behaviour */
    startUnlockChoice: (request: UnlockChoiceCtaRequest) => boolean;
}

export const useUnlockChoiceCta = (): IUseUnlockChoiceCta => {
    const dispatch = useAppDispatch();
    const viewType = useAppSelector((state) => state.viewType.current);
    const pageProperty = useAppSelector((state) => state.property.property);
    const {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {isResolved, isAssigned, isTreatment, trackExposure} = useUnlockChoiceExperiment({isEnabled: true});

    const isVerifiedUser = isUserLoggedIn && hasCompleteProfile && hasPhoneVerified;
    const isVariantDecided = isResolved && isAssigned;

    const getVerifiedContentFlow = (
        unlockChoiceEntryPoint: UnlockChoiceEntryPoint,
        ids: {propertyId?: number; offerId?: number; vendorId?: number},
        amplitudePlacement: AmplitudeAuthPlacement | undefined
    ): IStartRegistrationFlowPayload | null => {
        if (!isVerifiedUser || ids.offerId === undefined || !ids.vendorId) {
            return null;
        }

        return {
            isUserLoggedIn: true,
            hasCompleteProfile: true,
            hasPhoneVerified: true,
            unlockChoiceEntryPoint,
            unlockChoiceOrigin: "cta",
            propertyId: ids.propertyId,
            offerId: ids.offerId,
            vendorId: ids.vendorId,
            amplitudePlacement
        };
    };

    const getEligibleFlow = (request: UnlockChoiceCtaRequest): IStartRegistrationFlowPayload | null => {
        if (viewType !== ViewType.PROPERTY) {
            return null;
        }

        switch (request.kind) {
            case "lead": {
                const {payload, gtmClickText, gtmSourceSection} = request;

                if (payload.offerId === null || !payload.vendorId) {
                    return null;
                }

                return {
                    isUserLoggedIn,
                    hasCompleteProfile,
                    hasPhoneVerified,
                    userSource: UserSource.APPLICATION,
                    gtmClickText,
                    gtmSourceSection,
                    modalVariant: "hidden_prices_variant_b",
                    propertyId: payload.propertyId ?? undefined,
                    offerId: payload.offerId,
                    vendorId: payload.vendorId,
                    leadSource: payload.source,
                    leadSourceSection: payload.sourceSection,
                    amplitudePlacement: payload.amplitudePlacement,
                    unlockChoiceEntryPoint: UnlockChoiceEntryPoint.LEAD,
                    unlockChoiceOrigin: "cta",
                    unlockChoiceLeadPayload: payload
                };
            }
            case "floor_plan":
                if (!pageProperty || !pageProperty.plan || request.propertyId !== pageProperty.id) {
                    return null;
                }

                return getVerifiedContentFlow(
                    UnlockChoiceEntryPoint.FLOOR_PLAN,
                    {
                        propertyId: pageProperty.id,
                        offerId: pageProperty.offer.id,
                        vendorId: pageProperty.offer.vendor.id
                    },
                    "rp_download_floor_plan"
                );
            case "vendor_report":
                return getVerifiedContentFlow(
                    UnlockChoiceEntryPoint.VENDOR_REPORT,
                    {
                        propertyId: request.propertyId,
                        offerId: request.offerId,
                        vendorId: request.vendorId
                    },
                    request.amplitudePlacement
                );
            case "construction_photos":
                if (!pageProperty || !request.hasContent) {
                    return null;
                }

                return getVerifiedContentFlow(
                    UnlockChoiceEntryPoint.CONSTRUCTION_PHOTOS,
                    {
                        propertyId: pageProperty.id,
                        offerId: pageProperty.offer.id,
                        vendorId: pageProperty.offer.vendor.id
                    },
                    "rp_construction_photos"
                );
            default:
                return null;
        }
    };

    const isInterceptable = (request: UnlockChoiceCtaRequest) => isVariantDecided && isTreatment && getEligibleFlow(request) !== null;

    const startUnlockChoice = (request: UnlockChoiceCtaRequest) => {
        if (!isVariantDecided) {
            return false;
        }

        const flow = getEligibleFlow(request);

        if (flow === null) {
            return false;
        }

        trackExposure();

        if (!isTreatment) {
            return false;
        }

        dispatch(startRegistrationFlow(flow));

        return true;
    };

    return {isInterceptable, startUnlockChoice};
};
